import Vue from 'vue'
import Router from 'vue-router'
import { overviewRoutes } from './modules/performance/routes/dashboardRoutes'
import { cleanerActivityRoutes } from './modules/performance/routes/cleanerActivityRoutes'
import { padPresenceTestingRoutes } from './modules/pad-presence-testing/routes/padPresenceTestingRoutes'
import { surveysRoutes } from './modules/surveys/routes/surveysRoutes'
import { adminRoutes } from './modules/control-panel/routes/adminRoutes'
import { authRoutes } from './modules/auth/routes/authRoutes'
import { realTimeRoutes } from './modules/real-time/routes/realTimeRoutes'
import { workingAreaRoutes } from './modules/working-area/routes/workingAreaRoutes'
import { taskRoutes } from './modules/task/routes/taskRoutes'
import { usersRoutes } from './modules/users/routes/usersRoutes'
import { shiftRoutes } from './modules/shift/routes/shiftRoutes'
import { personalSettingsRoutes } from './modules/settings/routes/personalSettingsRoutes'
import { isPublicAuthRoute } from './modules/auth/authPublicRoutes'
import { hasRealTimeAccess } from './modules/common/consts/consts'
import store from './store'

Vue.use(Router)

const router = new Router({
	mode: 'history',
	base: process.env.BASE_URL,
	routes: [
		...overviewRoutes,
		...cleanerActivityRoutes,
		...padPresenceTestingRoutes,
		...surveysRoutes,
		...authRoutes,
		...adminRoutes,
		...realTimeRoutes,
		...shiftRoutes,
		...workingAreaRoutes,
		...taskRoutes,
		...usersRoutes,
		...personalSettingsRoutes,
		{ path: '/staff', name: 'staff' }
	]
})

router.beforeEach(async (to, from, next) => {
	// Redirect to login if the route doesn't have a name
	if (!to.name) return next('/login')

	// Allow direct access to unauthenticated auth routes
	if (isPublicAuthRoute(to.name)) return next()

	/** True for `/dashboard` and every nested dashboard tab (overview, performance, analytics, communication, room routes, …). */
	const isDashboardRoute = to.path.startsWith('/dashboard')

	// Check if the user is already logged in
	let user = store.getters.loggedUser

	// If the user is not logged in, attempt to retrieve the logged-in user
	if (!user) {
		try {
			await store.dispatch('getLoggedInUser')
		} catch (e) {
			console.error('[router] getLoggedInUser failed', e)
		}
		user = store.getters.loggedUser
		if (user) {
			try {
				await store.dispatch('loadFilterOptionsAndSelection')
			} catch (e) {
				console.error('[router] loadFilterOptionsAndSelection failed', e)
			}
		}
	}

	// Redirect to login if no user is found
	if (!user) return next('/login')

	// Safety net: if filters weren't loaded yet (e.g. SPA navigation to a dashboard tab without prior filter load).
	// Cleaner Activity lives on its own URL but depends on the same company/site/time filters.
	if (isDashboardRoute || to.name === 'cleanerActivity' || to.name === 'padPresenceTesting') {
		const filterState = store.state.filter
		if (!filterState?.filterOptions || !filterState?.filterSelected) {
			try {
				await store.dispatch('loadFilterOptionsAndSelection')
			} catch (e) {
				console.error('[router] loadFilterOptionsAndSelection (dashboard fallback) failed', e)
			}
		}
	}

	// Redirect from home to dashboard if user is logged in
	if (to.path === '/') return next('/dashboard')

	// Check if the user has the necessary roles to access the route
	let hasViewPermission = user.roles && user.roles.some((role) => to.path.split('/').includes(role))

	// Allow access to all dashboard routes (Overview, Performance, Analytics, Communication, etc.) for any logged-in user with dashboard access.
	// Tab visibility is controlled by Dashboard.vue (tabs array / site type); the router only needs to allow the path.
	if (isDashboardRoute) {
		hasViewPermission = true
	}

	// Either real-time tab permission opens Real time and the working areas it links to
	if (to.path.split('/').includes('real-time') && hasRealTimeAccess(user.roles)) {
		hasViewPermission = true
	}
	if (to.path.split('/').includes('working-area') && hasRealTimeAccess(user.roles)) {
		hasViewPermission = true
	}

	// Allow access to admin routes if user is admin type
	if (to.path.startsWith('/admin') && user.type === 'admin') {
		hasViewPermission = true
	}

	if (to.name === 'personalSettings') {
		hasViewPermission = true
	}

	// Redirect to the target route if the user has permission, otherwise redirect to login
	return hasViewPermission ? next() : next('/login')
})

// Track route navigation with Google Analytics
router.afterEach((to) => {
	const user = store.getters.loggedUser;
	if (user && to.name && to.name !== 'login' && window.gtag) {
		window.gtag('config', 'G-QF86M664LG', {
			page_path: to.path,
			page_title: to.name,
		});
	}
})

export default router
